import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";

import { AppThunk, RootState } from "@store";

import { createLoadableSelector, thunk } from "../utils";

const initialState = {
  variantByName: {} as Partial<Experiment.VariantByName>,
};

export type ExperimentState = typeof initialState;

const ExperimentSlice = createSlice({
  name: "Experiment",
  initialState,
  reducers: {
    updateVariantByName(
      state,
      { payload }: PayloadAction<ExperimentState["variantByName"]>
    ) {
      Object.assign(state.variantByName, payload);
    },
  },
});

const { actions } = ExperimentSlice;
export const {} = actions;
export default ExperimentSlice.reducer;

type ExperimentSelector<Variant extends "default" | "loadable"> = <
  Name extends Experiment.Name
>(
  name: Name
) => (
  state: RootState
) => {
  default: Experiment.VariantByName[Name] | Experiment.Control;
  loadable: [
    value: Experiment.VariantByName[Name] | Experiment.Control,
    loading: boolean
  ];
}[Variant];

type ExperimentThunk = <Name extends Experiment.Name>(
  name: Name
) => AppThunk<Promise<Experiment.VariantByName[Name]>>;

export const selectExperimentVariant = createLoadableSelector(
  <Name extends Experiment.Name>(name: Name) => (
    state: RootState
  ): Experiment.VariantByName[Name] | Experiment.Control =>
    state.experiment.variantByName[name] ?? "control",
  {
    loadAction: (name: Experiment.Name) => fetchExperimentVariant(name),
    selectLoaded: (name: Experiment.Name) => (state: RootState) =>
      name in state.experiment.variantByName,
  }
) as ExperimentSelector<"default"> & {
  load: ExperimentSelector<"loadable"> & {
    after: (condition: unknown) => ExperimentSelector<"loadable">;
  };
  loadAction: ExperimentThunk & {
    ifMissing: ExperimentThunk;
  };
};

export const fetchExperimentVariant = <Name extends Experiment.Name>(
  name: Name
) =>
  thunk((dispatch) =>
    webRPC.Experiments.getExperimentVariant({
      experimentNames: [name],
    }).then(
      handleProtoStatus({
        SUCCESS(data) {
          const variant = data.experimentVariants[
            name
          ] as Experiment.VariantByName[Name];
          dispatch(actions.updateVariantByName({ [name]: variant }));
          return variant;
        },
        _DEFAULT: handleFailedStatus("Failed to load experiment variant."),
      })
    )
  );

export namespace Experiment {
  export type Name = keyof VariantByName;
  export type Control = "control";

  export interface VariantByName {
    nonCreditLineSubscriptions: "small_entrypoint" | "large_entrypoint";
    hdyhauOnboardingExperiment: "control" | "grid" | "rows";
    onboardingCopy: "credit_plus" | "membership";
  }
}
